import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import * as PR from "../../prime-modules/index";
import { Link } from 'react-router-dom';
import { globalConfig, appName } from "../../GlobalConfig";
import { Formik, Form, Field, ErrorMessage, useFormik } from "formik";
import * as Yup from 'yup';
import * as utils from '../../utils';
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { setOrdersStatus, setPaymentStatus, setCustomers, setAffiliateUsers, setPaymentGateways, setPromoCodes, setProviderNames, setPurchaseDates, resetFilters, setFilterCounts, setKeywordsSearch, setPurchaseDatesFrom, setPurchaseDatesTo, setSimType } from "../../store/orderFilters";
import { initialState as orderFilterInitialState } from '../../store/orderFilters';
import { getPriceFilters, getPromoCodesList, getSettlementReport } from "../../services/api";
import { orderStatuses, paymentStatuses, deliveryStatuses, paymentGateways, formatDateTime, formatPromoCode, getCurrentTimestamp } from "../../utils/reuse";
import SendEmail from "../../utils/sendEmail";
import moment from "moment";
import { PERMISSIONS, hasPermission } from "../../utils/permissions";
import OrderActionMenu from "./OrderActionMenu";
import PriceDisplay from "../../components/PriceDisplay";


const OrdersList = (props) => {
    const dispatch = useDispatch();
    const toast = useRef();
    const op = useRef(null);
    const calendarRef = useRef(null);

    const adminData = useSelector(state => state.adminAuth.adminSessionData);
    const filters = useSelector(state => state?.orderFilters);
    const headers = useMemo(() => {
        return { sessionid: adminData.sessionId };
    }, [adminData.sessionId]);

    const currentDate = new Date();
    const [providerList, setProviderList] = useState([]);
    const [filterOpen, setFilterOpen] = useState(false);
    const [promoCodeOptions, setPromoCodeOptions] = useState([])
    const toggleFilter = () => setFilterOpen(!filterOpen);
    const [filtersVisible, setFiltersVisible] = useState(false);
    const [emailVisible, setEmailVisible] = useState(false);
    const [userId, setUserId] = useState({ userId: "", to: "" })
    const [appliedFilters, setAppliedFilters] = useState(filters);
    const [settlementReportVisible, setSettlementReportVisible] = useState(false);
    const affiliatesList = props.affiliatesList || [];

    const today = new Date();
    const currentYear = today.getFullYear();
    const prevMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);

    const initialReportValues = {
        reportType: "last",
        customMonth: prevMonthDate,
        affiliateId: ""
    };

    const reportValidationSchema = Yup.object().shape({
        reportType: Yup.string().required("Please select report type"),
        customMonth: Yup.date()
            .nullable()
            .when("reportType", {
                is: "custom",
                then: (schema) => schema.required("Please select month"),
                otherwise: (schema) => schema.nullable(),
            }),
    });

    const affiliateOptions = useMemo(() => {
        return (affiliatesList || []).map((aff) => ({
            label: aff.name || aff.affiliateName || aff.id,
            value: aff.id
        }));
    }, [affiliatesList]);

    const downloadSettlementReportHandler = (values, setSubmitting) => {
        const timeStamp = getCurrentTimestamp();
        let payload = {};

        if (values.reportType === "custom" && values.customMonth) {
            const selectedDate = new Date(values.customMonth);
            payload = {
                affiliateId: values.affiliateId || "",
                month: selectedDate.getMonth() + 1,
                year: selectedDate.getFullYear(),
            };
        } else if (values.reportType === "current") {
            const now = new Date();
            payload = {
                affiliateId: values.affiliateId || "",
                month: now.getMonth() + 1,
                year: now.getFullYear(),
            };
        } else if (values.reportType === "last") {
            const now = new Date();
            const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            payload = {
                affiliateId: values.affiliateId || "",
                month: lastMonth.getMonth() + 1,
                year: lastMonth.getFullYear(),
            };
        }

        getSettlementReport(payload, headers, dispatch, (response) => {
            if (response.result === "SUCCESS") {
                const csvData = response.data;
                const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
                const link = document.createElement("a");
                const url = URL.createObjectURL(blob);
                link.setAttribute("href", url);
                link.setAttribute("download", `bo.${appName}-settlement-report-${timeStamp}.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Settlement Report Exported successfully"
                });
            } else {
                const error = response.error || {};
                toast.current?.show({
                    severity: error.severity || "error",
                    summary: "Error",
                    detail: error.errorMsg ? error.errorMsg : error.summary || "Failed to download settlement report"
                });
            }
            setSubmitting(false);
        });
    };

    useEffect(() => {
        if (!hasPermission(adminData?.permission, PERMISSIONS.VIEW_PRICES)) return;
        getPriceFilters(headers, dispatch, response => {
            if (response.result === 'SUCCESS') {
                const providers = response.data?.providers?.length > 0 ? response.data?.providers.map(provider => ({ label: provider, value: provider })).sort((a, b) => a.label.localeCompare(b.label)) : [];
                setProviderList(providers);
            } else {
                const error = response?.error;
                toast.current?.show({ severity: error.severity, summary: 'Error', detail: (error?.errorMsg) ? error?.errorMsg : error?.summary })
            }
        })
    }, [dispatch, headers, adminData?.permission]);

    const getPromoCodes = useCallback(async (request = { startTs: '', endTs: '', status: '', type: 'percent,fixed' }) => {
        if (!hasPermission(adminData?.permission, PERMISSIONS.VIEW_PROMOS)) return;
        let getResponse = response => {
            if (response.result === "SUCCESS") {
                const promoCodesList = response.data
                    ? [...new Set(response.data.map(data => data.promoCode).filter(code => code && code.trim() !== ''))]
                        .sort((a, b) => a.localeCompare(b))
                    : [];
                setPromoCodeOptions(promoCodesList.map(code => ({ label: code, value: code })));
            }
        };
        getPromoCodesList(request, headers, dispatch, getResponse);
    }, [dispatch, headers, adminData?.permission]);

    useEffect(() => {
        if (hasPermission(adminData?.permission, PERMISSIONS.VIEW_PROMOS)) {
            getPromoCodes();
        }
    }, [getPromoCodes, adminData?.permission]);

    const validationSchema = Yup.object().shape({
        search: Yup.string().trim(),
    })

    const resetOrderForm = () => {
        dispatch(setKeywordsSearch(''))
        props.setPage(1);
        props.setOffset(0);
        props.setLimit(props.pageCount + 1);
        props.setListingType('list');
        props.setOrdersData(true);
    }

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        setFiltersVisible(false)
        const formData = new FormData(e.target);
        const formValues = Object.fromEntries(formData.entries())
        props.setPage(1);
        props.setOffset(0);
        props.setLimit(props.pageCount + 1);
        props.setListingType('list');
        props.setOrdersData(true);

        //Redux Store
        dispatch(setCustomers(trimFormData(formValues.customer)));
        dispatch(setAffiliateUsers(trimFormData(formValues.searchAffiliateUser)));
        dispatch(setOrdersStatus(filters?.ordersStatus));
        dispatch(setPaymentStatus(filters?.paymentStatus));
        dispatch(setProviderNames(filters?.providerNames));
        dispatch(setPaymentGateways(filters?.paymentGateways));
        dispatch(setPromoCodes(filters?.promoCodes));
        dispatch(setSimType(filters?.simType));
        dispatch(setFilterCounts(computeFilterCount()))
        setAppliedFilters(filters);
    }

    const handleFilterReset = () => {
        props.setPage(1);
        props.setOffset(0);
        props.setLimit(props.pageCount + 1);
        props.setListingType('list');
        props.setOrdersData(true);
        dispatch(resetFilters())
        setAppliedFilters(orderFilterInitialState)
    }

    const handleSubmit = (formData) => {
        dispatch(setKeywordsSearch(trimFormData(formData.search)))
        props.setPage(1);
        props.setOffset(0);
        props.setLimit(props.pageCount + 1);
        props.setListingType('list');
        props.setSearchTrigger(Date.now())
        props.setOrdersData(true);
    }

    const trimFormData = (val) => {
        return val.trim();
    }

    const formik = useFormik({
        initialValues: {
            search: filters?.keywordsSearch,
        },
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: handleSubmit
    })

    const handleSelectedDate = (e) => {
        if (e.value === props.nVal) {
            dispatch(setPurchaseDates(props.nVal))
            dispatch(setPurchaseDatesFrom(props.nVal))
            dispatch(setPurchaseDatesTo(props.nVal))
        } else {
            const date = e.value;
            const fromDate = utils.modifyDate(date[0]);
            const toDate = (date[1] === props.nVal) ? fromDate : utils.modifyDate(date[1]);
            dispatch(setPurchaseDates(date))
            dispatch(setPurchaseDatesFrom(fromDate))
            dispatch(setPurchaseDatesTo(toDate))
            // close the calendar if from and to date is selected
            if (date?.length > 1 && date[1] != null) {
                calendarRef.current.hide()
            }
        }
        props.setOrdersData(false);
    }

    const handleProviderChange = (e, provider) => {
        const isChecked = e.target.checked;
        let updatedProviders = [...filters?.providerNames];

        if (isChecked) {
            updatedProviders.push(provider);
        } else {
            updatedProviders = updatedProviders.filter(p => p !== provider);
        }
        dispatch(setProviderNames(updatedProviders))
        props.setOrdersData(false);
    };

    const handleSelectedOrderStatus = (val) => {
        dispatch(setOrdersStatus(val));
        props.setOrdersData(false);
    }

    const handleSelectedPaymentStatus = (val) => {
        dispatch(setPaymentStatus(val));
        props.setOrdersData(false);
    }

    const handleSelectedPaymentGateway = (val) => {
        dispatch(setPaymentGateways(val))
        props.setOrdersData(false);
    }

    const handleSimTypeChange = (val) => {
        const selectedType = val || '';
        dispatch(setSimType(selectedType));
        dispatch(setFilterCounts(computeFilterCount({ simType: selectedType })));
        setAppliedFilters((prev) => ({
            ...prev,
            simType: selectedType
        }));
        props.setPage(1);
        props.setOffset(0);
        props.setLimit(props.pageCount + 1);
        props.setListingType('list');
        props.setOrdersData(true);
    };

    const statusBgColor = (status) => {
        const statusVal = allOrderStatusList?.filter(val => val.value === status)[0]?.label;
        return (
            <p style={{ margin: "1px 0px 5px" }}>
                <span
                    className={`status ${status?.toLowerCase()}`}
                >{statusVal ?? status ?? 'N/A'}</span>
            </p>
        );
    };
    const usenameTemplate = rowData => {
        const userCustomers = '/users?email=' + encodeURIComponent(rowData.email);
        return (
            props.screen === 'user-orders'
                ? rowData.username
                : <Link to={userCustomers} className="underline">{rowData.username}</Link>
        )
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Order ID has been copied to clipboard.' });
        }).catch(err => {
            toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Failed to copy order ID.' });
        });
    };

    const copyToClipboardICCID = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            toast.current?.show({ severity: 'success', summary: 'Success', detail: 'ICCID has been copied to clipboard.' });
        }).catch(err => {
            toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Failed to copy ICCID.' });
        });
    };

    const copyToClipboardMSISDN = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            toast.current?.show({ severity: 'success', summary: 'Success', detail: 'MSISDN has been copied to clipboard.' });
        }).catch(err => {
            toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Failed to copy MSISDN.' });
        });
    };

    const geteSIMsDescription = (esim) => {
        const countryName = esim?.countries?.length ? esim?.countries[0]?.country?.name?.split("(")[0] : '';
        const description = countryName && esim.dataAmountForDisplay ? `${countryName} - ${esim.dataAmountForDisplay}` : esim.description;
        return description;
    }

    const handleEsimNavigation = (rowData) => {
        const iccid = rowData?.iccid;
        const orderId = rowData?.orderId;
        if (!iccid) {
            return
        }
        const newTabUrl = `/esim-details/${iccid}?orderId=${orderId}`;
        window.open(newTabUrl, '_blank');
    }

    const itemTemplate = (rowData) => {
        const refreshDataHandler = () => { props.setOrdersData(true); props.setSearchTrigger(Date.now()) };
        const displaySendEmailHandler = () => { setEmailVisible(true); setUserId({ userId: rowData?.userId, to: rowData?.email }) }
        return (
            <div className="col-12 custom-bg">
                <div className="custom-table-body">
                    <div className="table-grid">
                        <ul className="col-ul mobile-col-ul">
                            <li className="customer">
                                <div className="column-label">Customer</div>
                                <div className="column-data">
                                    {usenameTemplate(rowData)}
                                    <span className="iccid">
                                        <span className={rowData?.iccid ? "id-line" : "custom-target-icon"} onClick={() => handleEsimNavigation(rowData)}>{rowData?.iccid ? rowData.iccid : ""}</span>
                                        {rowData?.iccid ?
                                            <PR.Button
                                                icon="pi pi-copy"
                                                className="iccid-copy-button"
                                                tooltip="Copy ICCID"
                                                tooltipOptions={{ position: 'bottom' }}
                                                onClick={() => copyToClipboardICCID(rowData?.iccid)}
                                            /> : ""}
                                    </span>
                                    {rowData?.msisdn && <span className="iccid">
                                        {utils.formatUKPhoneNumber(rowData.msisdn)}
                                        <PR.Button
                                            icon="pi pi-copy"
                                            className="iccid-copy-button"
                                            tooltip="Copy MSISDN"
                                            tooltipOptions={{ position: 'bottom' }}
                                            onClick={() => copyToClipboardMSISDN(rowData.msisdn)}
                                        />
                                    </span>}
                                </div>
                            </li>
                            <li className="orderId">
                                <div className="column-label">Order ID</div>
                                <div className="column-data">
                                    <div>
                                        {rowData.orderId.substring(0, 8)}
                                        <PR.Button
                                            icon="pi pi-copy"
                                            onClick={() => copyToClipboard(rowData.orderId)}
                                            className="p-button-text icon-button"
                                            tooltip="Copy Order ID"
                                            tooltipOptions={{ position: 'left' }}
                                            style={{ color: 'red' }}
                                        />
                                    </div>
                                    {rowData?.subscriptionStatus ? (
                                        <span
                                            className="label recurring"
                                            style={{
                                                backgroundColor: rowData?.subscriptionStatus === 'ACTIVE' ? '#38a169' : '#e53e3e',
                                                color: '#fff', padding: '0px 6px', borderRadius: '4px', marginTop: '2px',
                                                display: 'inline-flex', alignItems: 'center'
                                            }}
                                        >
                                            <i className="pi pi-sync" style={{ marginRight: '4px', fontSize: '11px' }}></i>
                                            Recurring
                                        </span>
                                    ) : (String(rowData?.istopup) === 'true' || rowData?.istopup === true) ? (
                                        <span className="label topup" style={{ backgroundColor: '#0284c7', color: '#fff', padding: '0px 6px', borderRadius: '4px', marginTop: '2px', display: 'inline-flex', alignItems: 'center' }}>
                                            <i className="pi pi-arrow-up" style={{ marginRight: '4px', fontSize: '11px' }}></i>
                                            Topup
                                        </span>
                                    ) : (
                                        <span className="label regular" style={{ backgroundColor: '#e2e8f0', color: '#4a5568', padding: '0px 6px', borderRadius: '4px', marginTop: '2px', display: 'inline-flex', alignItems: 'center' }}>
                                            <i className="pi pi-save" style={{ marginRight: '4px', fontSize: '11px' }}></i>
                                            Regular
                                        </span>
                                    )}
                                </div>
                            </li>

                            {/* <li className="affiliateUser">
                                <div className="column-label">ICCID</div>
                                <div className="column-data">{rowData?.iccid ? rowData.iccid : "-"}</div>
                            </li> */}
                            <li className="bundleName">
                                <div className="column-label">Bundle Name</div>
                                <div className="column-data" >{geteSIMsDescription(rowData?.dataPlans.length && rowData.dataPlans[0])}</div>
                            </li>
                            <li className="price">
                                <div className="column-label">Total Price</div>
                                <div className="column-data">
                                    <PriceDisplay rowData={rowData} />
                                </div>
                            </li>

                            <li className="promoCode">
                                <div className="column-label">Promocode</div>
                                <div className="column-data">
                                    {rowData.promoCode && (
                                        <>
                                            <span
                                                className="promo-tooltip"
                                                data-pr-tooltip={rowData.discountValue ? `Discount Value: ${rowData.discountValue}` : rowData.paymentGateway === "prepaid" ? "Prepaid Voucher" : "-"}
                                                data-pr-position="bottom"
                                            >
                                                {formatPromoCode(rowData?.promoCode, rowData.paymentGateway === 'prepaid') || " - "}
                                                <PR.Button
                                                    icon="pi pi-info-circle"
                                                    className="icon-button"
                                                    onMouseEnter={(e) => handleMouseEnter(e, rowData)}
                                                    onMouseLeave={handleMouseLeave}
                                                />
                                            </span>
                                           
                                            <PR.OverlayPanel
                                                ref={op}
                                                dismissable
                                                style={{
                                                    backgroundColor: "#3a4668",
                                                    borderRadius: "8px",
                                                }}
                                                className="promoCode-tooltip-new"
                                            >
                                                {renderPromoContent()}
                                            </PR.OverlayPanel>
                                        </>
                                    )}
                                </div>
                            </li>
                            <li className="providerName">
                                <div className="column-label">Provider</div>
                                <div className={`column-data ${rowData?.dataPlans?.some(plan => plan?.simtype === "pSIM") ? "physical-sim-provider" : "provider-center"}`}>
                                    <div>
                                        {rowData.providerName}
                                        {(appName === 'elefone' || appName === 'briant') && (() => {
                                            let bundleType = rowData?.dataPlans?.[0]?.bundleType;
                                            if (!bundleType) {
                                                bundleType = '';
                                            };
                                            const displayProvider = bundleType.toLowerCase();
                                            let iconClass = "";
                                            let tooltip = "";
                                            if (displayProvider === "domestic") {
                                                iconClass = "pi pi-home";
                                                tooltip = "Domestic";
                                            } else if (displayProvider === "travel" && (rowData?.istopup === true || String(rowData?.istopup) === 'true')) {
    iconClass = "pi pi-arrow-up";
    tooltip = "Topup";
} else if (displayProvider === "travel" && rowData?.istopup === false) {
    iconClass = "pi pi-send";
    tooltip = "Travel";
} else {
    return null;
}
return <i className={`${iconClass} ml-2`} style={{ color: 'red', fontSize: '13px' }} title={tooltip}></i>;
                                        })()}
                                    </div>
                                    {rowData?.dataPlans?.some(plan => plan?.simtype === "pSIM") && (
                                        <div className="sim-indicator physical-sim">
                                            <i className="pi pi-mobile mr-1" />
                                            Physical SIM
                                        </div>
                                    )}
                                </div>
                            </li>
                            <li className="createdTs">
                                <div className="column-label">Date & time</div>
                                <div className="column-data">{formatDateTime(rowData.createdTs)}</div>
                            </li>
                            <li className="paymentGateway">
                                <div className="column-label">Payment Status</div>
                                <div className="column-data">
                                    <span className="payment-column-font">{rowData.paymentGateway ? utils.firstLetterCapital(rowData.paymentGateway) : "-"}</span>
                                    {statusBgColor(rowData?.paymentStatus)}
                                </div>
                            </li>
                            {/* <li className="paymentStatus">
                                <div className="column-label">Payment Status</div>
                                <div className="column-data">{statusBgColor(rowData?.paymentStatus)}</div>
                            </li> */}
                            <li className="orderStatus">
                                <div className="column-label">Order Status</div>
                                <div className="column-data">{statusBgColor(rowData?.orderStatus)}</div>
                            </li>
                            <li className="affiliateUser">
                                <div className="column-label">Affiliate Name</div>
                                <div className="column-data">{rowData.affiliateName ? rowData.affiliateName : "-"}</div>
                            </li>
                            <li className="orderActions">
                                <div className="column-label">Actions</div>
                                <div className="column-data flex">
                                    <OrderActionMenu orders={rowData} refreshData={refreshDataHandler} displaySendEmailHandler={displaySendEmailHandler} toast={toast} />
                                
                                    <div className="orderDetailArrow" onClick={() => window.open('/' + props.screen + '/details/' + rowData.orderId, '_blank')}
                                    >
                                        <PR.Button
                                            icon="pi pi-chevron-right"
                                            className="p-button-text icon-button"
                                            tooltip="View Order Details"
                                            tooltipOptions={{ position: 'left' }}
                                            style={{ color: 'red' }}
                                        />
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        );
    };

    const paginate = (currentPage) => {
        props.setPaging(false);
        props.setPage(currentPage);
        const offsetVal = (currentPage - 1) * props.pageCount;
        const limitVal = props.pageCount + 1;
        props.setOffset(offsetVal);
        props.setLimit(limitVal);
        props.setOrdersData(true);
        props.setListingType('list');
    };

    const onSortChange = (value) => {
        if (value.indexOf("!") === 0) {
            props.setSortOrder(-1);
            props.setSortField(value.substring(1, value.length));
            props.setSortKey(value);
        } else {
            props.setSortOrder(1);
            props.setSortField(value);
            props.setSortKey(value);
        }
        props.setPage(1);
        props.setOffset(0);
        props.setLimit(props.pageCount + 1);
        props.setListingType('list');
        props.setOrdersData(true);
    };

    const handleAffiliateOptions = (customers = []) => {
        return (Array.isArray(customers) ? customers : [])
            .map(customer => customer.trim())
            .sort((a, b) => a.localeCompare(b));
    };

    const handlePromocodeChange = (val) => {
        const selectedCodes = val || '';
        dispatch(setPromoCodes(selectedCodes))
        props.setOrdersData(false);
    };


    const ordersDataHeader = (field, label, selector, sortable = true) => {
        return (
            sortable ?
                (props.sortField !== field) ?
                    <>
                        <li onClick={(e) => onSortChange(field)} className={selector}><span>{label}<i className="pi pi-sort-alt"></i></span> </li>
                    </> :
                    <>
                        {(props.sortKey === field) ?
                            <>
                                <li onClick={(e) => onSortChange('!' + field)} className={selector}><span className="selectedList">{label}<i className="pi pi-sort-amount-up-alt"></i></span> </li>
                            </> :
                            <li onClick={(e) => onSortChange(field)} className={selector}><span className="selectedList">{label}<i className="pi pi-sort-amount-down"></i></span> </li>
                        }
                    </>
                :
                <li className={selector} style={{ pointerEvents: 'none' }}>
                    <span>{label}</span>
                </li>
        );
    }

    const orderStatusList = orderStatuses;
    const paymentStatusList = paymentStatuses;
    const deliveryStatusList = (appName === 'elefone' || appName === 'briant') ? deliveryStatuses : [];
    const allOrderStatusList = [...orderStatuses, ...deliveryStatuses, ...paymentStatuses];
    const targetStatusList = [...orderStatusList, ...deliveryStatusList];
    const paymentGatewayOptions = paymentGateways;

    const handleCustomer = (e) => {
        dispatch(setCustomers(e.target.value))
        formik.handleChange(e)
        props.setOrdersData(false);
    }

    const handleKeywordSearch = (e) => {
        dispatch(setKeywordsSearch(e.target.value))
        formik.handleChange(e)
        props.setOrdersData(false);
    }

    const handleAffiliateName = (e) => {
        dispatch(setAffiliateUsers(e.target.value))
        formik.handleChange(e)
        props.setOrdersData(false);
    }

    const computeFilterCount = (overrides = {}) => {
        const {
            providerName = filters?.providerNames,
            orderStatus = filters?.ordersStatus,
            paymentStatus = filters?.paymentStatus,
            paymentGateway = filters?.paymentGateways,
            customer = filters?.customers,
            purchaseDate = filters?.purchaseDates,
            affiliateUser = filters?.affiliateUsers,
            promoCode = filters?.promoCodes,
            simType = filters?.simType
        } = overrides;
    
        let count = 0;
        count += providerName.length;
        count += orderStatus.length;
        count += (paymentStatus?.length || 0);
        count += paymentGateway.length;
        if (customer) count++;
        if (purchaseDate) count++;
        if (affiliateUser) count++;
        if (promoCode) count++;
        if (simType) count++;
    
        return count;
    };

    const getFilterSummary = (filters, orderStatusList) => {
        const summaryParts = [];
    
        if (filters.customers) {
             summaryParts.push({ label: 'Customer', value: filters.customers });
        }

        if (filters.simType) {
             summaryParts.push({ label: 'SIM Type', value: filters.simType === 'DOMESTIC' ? 'Domestic' : 'Travel' });
        }

        if (filters?.purchaseDates?.length === 2) {
            const [from, to] = filters?.purchaseDates;
            const formattedFrom = moment(from).format("DD MMM YYYY");
            const formattedTo = moment(to).format("DD MMM YYYY");
            
            summaryParts.push({
                label: 'Purchase Date',
                value: formattedTo !== "Invalid date" ? `${formattedFrom} - ${formattedTo}` : formattedFrom
            });
        }

        if (filters.affiliateUsers) {
            summaryParts.push({ label: 'Affiliate', value: filters.affiliateUsers });
        }

        if (filters.promoCodes) {
            summaryParts.push({ label: 'Promo Code', value: filters.promoCodes });
        }

        if (filters.providerNames.length > 0) {
            summaryParts.push({ label: 'Provider', value: filters.providerNames.join(', ') });

        }
    
        if (filters?.ordersStatus?.length > 0) {
            const statusLabels = orderStatusList
                .filter(status => filters.ordersStatus.includes(status.value))
                .map(status =>
                    status.label.charAt(0).toUpperCase() + status.label.slice(1).toLowerCase()
                )
                .join(', ');
            summaryParts.push({ label: 'Order Status', value: statusLabels });
        }

        if (filters?.paymentStatus?.length > 0) {
            const statusLabels = paymentStatusList
                .filter(status => filters.paymentStatus.includes(status.value))
                .map(status =>
                    status.label.charAt(0).toUpperCase() + status.label.slice(1).toLowerCase()
                )
                .join(', ');
            summaryParts.push({ label: 'Payment Status', value: statusLabels || filters.paymentStatus.join(', ') });
        }
    
        if (filters?.paymentGateways?.length > 0) {
            summaryParts.push({ label: 'Payment Gateway', value: filters.paymentGateways.join(', ') });
        }
        return summaryParts;
    };

    const filterSummary = getFilterSummary(appliedFilters, allOrderStatusList)



    const [hoveredRow, setHoveredRow] = React.useState(null);


    const handleMouseEnter = (event, rowData) => {
        op.current.show(event);
        setHoveredRow(rowData);
    };

    const handleMouseLeave = () => {
        op.current.hide();
        setHoveredRow(null);
    };



    const renderPromoContent = () => {
        if (!hoveredRow) return null;
        if (hoveredRow.paymentGateway === "prepaid") {
            return (
                <div style={{ marginBottom: '0.05rem', color: "#fff", fontSize: "13px" }}>
                    <p>
                        <strong style={{ textDecoration: 'underline' }}>Prepaid Voucher</strong>
                    </p>
                    <p className="mt-2">
                        <strong>Batch Name:</strong> {hoveredRow.promoCodeBatchName || "N/A"}
                    </p>
                    <p className="mt-2">
                        <strong>Issuer Name:</strong> {hoveredRow.promoCodeOwnerName || "N/A"}
                    </p>
                </div>
            );
        }

        return (
            <div style={{ marginBottom: '0.05rem', color: "#fff", fontSize: "13px" }}>
                <p>
                    <strong style={{ textDecoration: 'underline' }}>Discount Voucher</strong>
                </p>
                <p className="mt-2">
                    <strong>Type of voucher:</strong>
                    {hoveredRow.isBundleSpecific ? "Bundle Specific" : "Regular"}
                </p>
            </div>
        );
    };



    return (
        <section className="admin-orderlist-section">
            <div className="grid grid-nogutter">
                <div className="col-12">
                    <div className="heading-sec align-items-center justify-content-between">
                        <div className="flex align-items-center justify-content-between mb-5 mobile-margin">
                            <div className="right flex align-items-center ">
                                <h2>Orders</h2>
                                <div className="breadcrumb-section">
                                    <PR.BreadCrumb model={props.items} home={props.home} />
                                </div>
                            </div>
                            <div className="right-section flex align-items-right gap-3" >
                                <PR.Tooltip target=".export-tooltip" content={!filters?.purchaseDates ? 'Select Date Range to Export CSV' : undefined} position="top" showOnDisabled />
                                {hasPermission(adminData.permission, PERMISSIONS.MANAGE_ORDERS) && (
                                    <PR.Button label="Export CSV" className="exportBtn export-tooltip" icon="pi pi-file-export" iconPos="left" onClick={() => props.setListingType('export')}
                                        disabled={!(filters?.filterCounts > 0 && appliedFilters?.purchaseDates) || props?.loading}
                                    />
                                )}
                                {appName !== 'data2go-iot' && hasPermission(adminData.permission, PERMISSIONS.MANAGE_ORDERS) && (
                                    <PR.Button
                                        label="Settlement Report"
                                        className="exportBtn"
                                        icon="pi pi-download"
                                        iconPos="left"
                                        onClick={() => setSettlementReportVisible(true)}
                                        disabled={props?.loading}
                                    />
                                )}
                                <PR.Button text className="filter-toggle-button" onClick={toggleFilter}>
                                    <i className={`pi ${filterOpen ? "pi-filter-slash" : "pi-filter-fill"}`}></i>
                                </PR.Button>
                            </div>
                        </div>
                        <div className={`filter-right ${filterOpen ? "mobile-filter-section" : ""}`}>
                            <form autoComplete="off" onSubmit={formik.handleSubmit} className="email-orderid-iccid">
                                <div className='flex align-items-center mobile-flex'>
                                    <div className="users-search-input">
                                        <span>
                                            <PR.InputText placeholder="Search by Customer Email, Order ID, ICCID or MSISDN" name="search" onChange={handleKeywordSearch} onBlur={formik.handleBlur} value={filters?.keywordsSearch} autoComplete="off" />
                                        </span>
                                    </div>

                                    <div className="users-search-input last-buttons-block">
                                        <span>
                                            <PR.Button label="Search" type='submit' className="searchBtn" />
                                            <PR.Button label="Reset" type='reset' className="resetBtn ml-1" onClick={resetOrderForm} />
                                        </span>
                                    </div>
                                </div>
                            </form>
                            <div className="flex align-items-center gap-3 mobile-filters">
                                {(appName === 'elefone' || appName === 'briant') && (
                                    <div className="flex align-items-center">
                                        <PR.Dropdown
                                            value={filters?.simType || ''}
                                            options={[
                                                { label: "Domestic", value: "DOMESTIC" },
                                                { label: "Travel", value: "TRAVEL" }
                                            ]}
                                            onChange={(e) => handleSimTypeChange(e.value)}
                                            placeholder="Select SIM Type"
                                            showClear
                                            className="sim-type-dropdown"
                                            style={{ minWidth: '100px' }}
                                        />
                                    </div>
                                )}
                                <div className="flex align-items-center">
                                    <PR.Checkbox
                                        inputId="selectAllStatus"
                                        checked={filters?.ordersStatus.length === targetStatusList.length && targetStatusList.every(status => filters?.ordersStatus.includes(status.value))}
                                        onChange={(e) => {
                                            props.setListingType("list")
                                            props.setPage(1);
                                            if (e.checked) {
                                                const allValues = targetStatusList.map(status => status.value);
                                                dispatch(setFilterCounts(computeFilterCount({ orderStatus: allValues })));
                                                dispatch(setOrdersStatus(allValues));
                                                setAppliedFilters((prev) => ({
                                                    ...prev,
                                                    ordersStatus: allValues
                                                }));
                                            } else {
                                                dispatch(setFilterCounts(computeFilterCount({ orderStatus: [] })));
                                                dispatch(setOrdersStatus([]));
                                                setAppliedFilters((prev) => ({
                                                    ...prev,
                                                    ordersStatus: []
                                                }));
                                            }
                                        }}
                                    />
                                    <label className="ml-2 order-status-label">All Order Statuses</label>
                                </div>
                                <PR.Button
                                    icon="pi pi-filter"
                                    label={filters?.filterCounts > 0 ? <>Filter <span className="count">{filters?.filterCounts}</span></> : "Filter"}
                                    className="exportBtn filter-btn"
                                    onClick={() => setFiltersVisible(true)}
                                    disabled={props.loading}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="applied-filter">
                        <span className="red-color"><strong>Applied Filters : </strong></span>
                        {filterSummary.length > 0 ? (
                            filterSummary.map((item, index) => (
                                <span key={index} style={{ margin: "0px 5px 0px 0px" }}>
                                    <strong>{item.label}:</strong> <span style={{ color: "#7e7373" }}>{item.value}</span>
                                    {index !== filterSummary.length - 1 && ' | '}
                                </span>
                            ))
                        ) : (
                            <span>No Filters Selected</span>
                        )}

                        {/* <span className="red-color">Applied Filters : </span>{filterSummary ? filterSummary : "No Filters Selected"} */}
                    </div>
                </div>
            </div>
            <div className="users-data-table card">
                <div className="card datatable-scroll">
                    <div className="custom-table">
                        <div className="custom-table-header">
                            <div className="table-grid">
                                <ul className="col-ul sorting-li">
                                    {ordersDataHeader('name', 'Customer', 'customer')}
                                    {ordersDataHeader('orderId', 'Order ID', 'orderId')}
                                    {/* {ordersDataHeader('iccid', 'ICCID', 'iccid', false)} */}
                                    {ordersDataHeader('bundleName', 'Bundle Name', 'bundleName', false)}
                                    {ordersDataHeader('totalOrderAmount', 'Total Price', 'price')}
                                    {ordersDataHeader('promoCode', 'Promocode', 'promoCode')}
                                    {ordersDataHeader('providerName', 'Provider', 'providerName')}
                                    {ordersDataHeader('createdTs', 'Date & time', 'purchasedOn')}
                                    {/* {ordersDataHeader('paymentGateway', 'Payment Method', 'paymentGateway')} */}
                                    {ordersDataHeader('paymentStatus', 'Payment Status', 'paymentStatus')}
                                    {ordersDataHeader('orderStatus', 'Order Status', 'orderStatus')}
                                    {ordersDataHeader('affiliateName', 'Affiliate Name', 'affiliateUser')}
                                    {ordersDataHeader('actions', 'Actions', 'actions', false)}
                                </ul>
                            </div>
                        </div>
                        <PR.DataView
                            loading={props.loading}
                            value={props.orders}
                            itemTemplate={itemTemplate}
                            emptyMessage={"No Results Found"}
                        />
                        <PR.Tooltip target=".promo-tooltip" className="promocode-tooltip" />
                    </div>
                </div>
            </div>

            <PR.Dialog header="Filter Options" visible={filtersVisible} onHide={() => { if (!filtersVisible) return; setFiltersVisible(false); }} draggable={false} blockScroll={true} className="filter-dialog order-filters-dialog">
                <form autoComplete="off" className="mt-0" onSubmit={handleFilterSubmit}>
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <div className="flex flex-column gap-2">
                                <label htmlFor="customer" className="title-label">Customer Name</label>
                                <PR.InputText placeholder="Filter by customer name" name="customer" inputId="customer" onChange={handleCustomer} onBlur={formik.handleBlur} value={filters?.customers} autoComplete="off" />
                            </div>
                        </div>
                        <div className="col-12 md:col-6">
                            <div className="flex flex-column gap-2">
                                <label htmlFor="searchPurchaseDate" className="title-label">Date Range</label>
                                <PR.Calendar panelClassName="datte-range-calendar" inputId="searchPurchaseDate" value={filters?.purchaseDates} onChange={handleSelectedDate} selectionMode="range" readOnlyInput placeholder="Select date range" name="searchPurchaseDate" dateFormat="dd-mm-yy" maxDate={currentDate} showIcon showButtonBar numberOfMonths={2} ref={calendarRef} />
                            </div>
                        </div>
                    </div>

                    <div className="grid mt-1">
                        <div className="col-12 md:col-6">
                            <div className="flex flex-column gap-2">
                                <label htmlFor="searchAffiliateUser" className="title-label">Affiliate Name</label>
                                <PR.Dropdown filter resetFilterOnHide showClear placeholder="Select affiliate name" inputId="searchAffiliateUser" name="searchAffiliateUser" options={handleAffiliateOptions(props.affiliateNames)} onChange={handleAffiliateName} onBlur={formik.handleBlur} value={filters?.affiliateUsers || ''} autoComplete="off" />
                            </div>
                        </div>
                        <div className="col-12 md:col-6">
                            <div className="flex flex-column gap-2">
                                <label htmlFor="searchPromocode" className="title-label">Promo code</label>
                                <PR.Dropdown filter resetFilterOnHide showClear value={filters?.promoCodes || ''} inputId="searchPromocode" options={promoCodeOptions} onChange={(e) => handlePromocodeChange(e.value)} name="searchPromocode" optionLabel="label" placeholder="Select promo code" />
                            </div>
                        </div>
                    </div>

                    <div className="grid mt-1">
                        {(appName === 'elefone' || appName === 'briant') && (
                            <div className="col-12 md:col-6">
                                <div className="flex flex-column gap-2">
                                    <label htmlFor="searchSimType" className="title-label">SIM Type</label>
                                    <PR.Dropdown
                                        value={filters?.simType || ''}
                                        options={[
                                            { label: "Domestic", value: "DOMESTIC" },
                                            { label: "Travel", value: "TRAVEL" }
                                        ]}
                                        onChange={(e) => {
                                            dispatch(setSimType(e.value || ''));
                                            props.setOrdersData(false);
                                        }}
                                        showClear
                                        placeholder="Select SIM Type"
                                        inputId="searchSimType"
                                        name="searchSimType"
                                    />
                                </div>
                            </div>
                        )}
                        {providerList.length > 0 && (
                            <div className={(appName === 'elefone' || appName === 'briant') ? "col-12 md:col-6" : "col-12"}>
                                <div className="flex flex-column gap-2">
                                    <label htmlFor="searchProvider" className="title-label">Provider</label>
                                    <div className="flex gap-3 flex-wrap align-items-center" style={{ minHeight: '40px' }}>
                                        {
                                            providerList.map((provider, index) =>
                                                <div className="flex align-items-center mr-2" key={index}>
                                                    <PR.Checkbox inputId={`provider${index}`} checked={filters?.providerNames.includes(provider.value)} onChange={(e) => handleProviderChange(e, provider.value)}></PR.Checkbox>
                                                    <label htmlFor={`provider${index}`} className="ml-1 cursor-pointer">{provider.label}</label>
                                                </div>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {orderStatusList.length > 0 && <div className="card mt-4">
                        <label htmlFor="username" className="title-label">Order Status</label>
                        <div className="flex gap-3 flex-wrap mt-3">
                            {
                                orderStatusList.map((status) => (
                                    <div className="flex mr-2" key={status.value}>
                                        <PR.Checkbox inputId={status.value} value={status.value} checked={filters?.ordersStatus.includes(status.value)} onChange={(e) => handleSelectedOrderStatus(e.checked ? [...filters?.ordersStatus, e.value] : filters?.ordersStatus.filter(val => val !== e.value))}></PR.Checkbox>
                                        <label htmlFor={status.value} className="ml-1">{status.label}</label>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                    }

                    {paymentStatusList.length > 0 && <div className="card mt-4">
                        <label htmlFor="paymentStatus" className="title-label">Payment Status</label>
                        <div className="flex gap-3 flex-wrap mt-3">
                            {
                                paymentStatusList.map((status) => (
                                    <div className="flex mr-2" key={status.value}>
                                        <PR.Checkbox
                                            inputId={`payStatus_${status.value}`}
                                            value={status.value}
                                            checked={filters?.paymentStatus?.includes(status.value)}
                                            onChange={(e) => handleSelectedPaymentStatus(e.checked ? [...(filters?.paymentStatus || []), e.value] : (filters?.paymentStatus || []).filter(val => val !== e.value))}
                                        ></PR.Checkbox>
                                        <label htmlFor={`payStatus_${status.value}`} className="ml-1">{status.label}</label>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                    }

                    {deliveryStatusList.length > 0 && <div className="card mt-4">
                        <label htmlFor="username" className="title-label">Delivery Status</label>
                        <div className="flex gap-3 flex-wrap mt-3">
                            {
                                deliveryStatusList.map((status) => (
                                    <div className="flex mr-2" key={status.value}>
                                        <PR.Checkbox inputId={status.value} value={status.value} checked={filters?.ordersStatus.includes(status.value)} onChange={(e) => handleSelectedOrderStatus(e.checked ? [...filters?.ordersStatus, e.value] : filters?.ordersStatus.filter(val => val !== e.value))}></PR.Checkbox>
                                        <label htmlFor={status.value} className="ml-1">{status.label}</label>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                    }

                    {paymentGatewayOptions.length > 0 && <div className="card mt-4">
                        <label htmlFor="username" className="title-label">Payment Method</label>
                        <div className="flex gap-3 amount-data-options flex-wrap mt-3">
                            {
                                paymentGatewayOptions.map((gateway) => (
                                    <div className="flex mr-2" key={gateway.value}>
                                        <PR.Checkbox inputId={gateway.value} value={gateway.value} checked={filters?.paymentGateways.includes(gateway.value)} onChange={(e) => handleSelectedPaymentGateway(e.checked ? [...filters?.paymentGateways, e.value] : filters?.paymentGateways.filter(val => val !== e.value))}></PR.Checkbox>
                                        <label htmlFor={gateway.value} className="ml-1">{gateway.label}</label>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                    }
                    <div className="buttons-sections flex align-items-center justify-content-end gap-3">
                        <PR.Button label="Reset" type="button" className="confirm-button reset-btn min-width" onClick={handleFilterReset} />
                        <PR.Button label="Apply Filters" type="submit" className="confirm-button min-width" />
                    </div>
                </form>
            </PR.Dialog>
            {props.orders.length > 0 ? (
                <div className="pagination">
                    <button
                        type="button"
                        onClick={() => paginate(props.page - 1)}
                        className={props.paging ? props.page <= 1 ? "disabled" : "pagination-button" : "disabled"}
                    >
                        {globalConfig.pagination_Previous}
                    </button>
                    <span className="active"> {props.page} </span>
                    <button
                        type="button"
                        onClick={() => paginate(props.page + 1)}
                        className={props.paging ? props.last ? "disabled" : "pagination-button" : "disabled"}
                    >
                        {globalConfig.pagination_Next}
                    </button>
                </div>
            ) : (
                <></>
            )}
            <PR.Toast ref={toast} position='top-right' />

            <SendEmail
                visible={emailVisible}
                userId={userId}
                onClose={() => setEmailVisible(false)}
            />

            <PR.Dialog
                header={"Download Settlement Report"}
                visible={settlementReportVisible}
                draggable={false}
                resizable={false}
                onHide={() => setSettlementReportVisible(false)}
                dismissableMask={true}
                maskClassName="create-voucher-dialog-mask"
                className="filter-dialog order-filters-dialog report-download-dialog"
            >
                <div className="card">
                    <p className="text-sm text-gray-500 mb-4 p-text-tag">
                        Please choose an option to download.
                    </p>
                    <Formik
                        initialValues={initialReportValues}
                        validationSchema={reportValidationSchema}
                        onSubmit={(values, { setSubmitting }) => {
                            downloadSettlementReportHandler(values, setSubmitting);
                        }}
                    >
                        {({ values, setFieldValue, isSubmitting }) => (
                            <Form className="flex flex-column gap-3">
                                {/* Radio Buttons Section */}
                                <div className="flex flex-column gap-3">
                                    <div className="flex align-items-center gap-2">
                                        <Field
                                            type="radio"
                                            name="reportType"
                                            value="last"
                                            as={PR.RadioButton}
                                            inputId="lastMonthSettlement"
                                        />
                                        <label htmlFor="lastMonthSettlement" className="cursor-pointer">Last month</label>
                                    </div>

                                    <div className="flex align-items-center gap-2">
                                        <Field
                                            type="radio"
                                            name="reportType"
                                            value="current"
                                            as={PR.RadioButton}
                                            inputId="currentMonthSettlement"
                                        />
                                        <label htmlFor="currentMonthSettlement" className="cursor-pointer">Current month</label>
                                    </div>

                                    <div className="flex align-items-center gap-2">
                                        <Field
                                            type="radio"
                                            name="reportType"
                                            value="custom"
                                            as={PR.RadioButton}
                                            inputId="customSettlement"
                                        />
                                        <label htmlFor="customSettlement" className="cursor-pointer">Custom</label>
                                    </div>

                                    <ErrorMessage
                                        name="reportType"
                                        component="div"
                                        className="p-error text-sm mt-1"
                                    />
                                </div>

                                {/* Custom Date Field */}
                                {values.reportType === "custom" && (
                                    <div className="mt-2">
                                        <PR.Calendar
                                            id="customMonthSettlementCal"
                                            value={values.customMonth}
                                            onChange={(e) => setFieldValue("customMonth", e.value)}
                                            view="month"
                                            dateFormat="mm/yy"
                                            yearNavigator
                                            monthNavigator
                                            yearRange={`2024:${currentYear}`}
                                            minDate={new Date(2024, 0, 1)}
                                            maxDate={new Date()}
                                            showIcon
                                            placeholder="Select Month/Year"
                                        />
                                        <ErrorMessage
                                            name="customMonth"
                                            component="div"
                                            className="p-error text-sm mt-1"
                                        />
                                    </div>
                                )}

                                {/* Optional Affiliate Selector */}
                                {affiliateOptions.length > 0 && (
                                    <div className="flex flex-column gap-1 mt-2">
                                        <label className="text-sm text-gray-600 font-semibold">Affiliate (Optional)</label>
                                        <PR.Dropdown
                                            filter
                                            resetFilterOnHide
                                            showClear
                                            filterBy="label"
                                            value={values.affiliateId || ''}
                                            options={affiliateOptions}
                                            onChange={(e) => setFieldValue("affiliateId", e.value || '')}
                                            placeholder="Select affiliate name"
                                            className="w-full"
                                        />
                                    </div>
                                )}

                                {/* Download Button & Cancel Button */}
                                <div className="text-right mt-4 buttons-sections">
                                    <PR.Button
                                        type="button"
                                        label="Cancel"
                                        className="confirm-button reset-btn min-width mr-3"
                                        onClick={() => setSettlementReportVisible(false)}
                                    />
                                    <PR.Button
                                        type="submit"
                                        label={isSubmitting ? "Downloading..." : "Download"}
                                        className="confirm-button min-width"
                                        disabled={isSubmitting}
                                    />
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </PR.Dialog>
        </section>
    );
}

export default OrdersList;
